import {
  CabinOverridesPayload,
  CharterCabinClass,
  SeatConfiguration,
  SeatInfo,
  SeatLayoutEntry,
  SeatMapRow,
} from '../charter.types';

import {
  collectFloatingFacilityRows,
  expandRowSelection,
  resolveRowLayouts,
  resolveSeatOverrides,
} from './seatRowLayout';

const STANDARD_SEAT_FEATURE = {
  isExitRow: false,
  hasExtraLegroom: false,
};

const extractSeatLetters = (seatLayout: Array<SeatLayoutEntry>): Array<string> =>
  seatLayout.reduce<Array<string>>((letters, entry) => {
    if (entry.kind === 'seat') {
      letters.push(entry.code);
    }

    return letters;
  }, []);

const buildSeatInfo = (
  cabin: CharterCabinClass,
  rowNumber: number,
  seatLetter: string,
  overrides: Array<CabinOverridesPayload['overrides'][number]>,
) => {
  let feature = { ...STANDARD_SEAT_FEATURE };
  let price = 0;
  let isAvailable = true;

  overrides.forEach((override) => {
    if (override.feature) {
      feature = { ...feature, ...override.feature };
    }

    if (typeof override.price === 'number') {
      price = override.price;
    }

    if (typeof override.isAvailable === 'boolean') {
      isAvailable = override.isAvailable;
    }
  });

  const seatInfo: SeatInfo = {
    seatId: `${rowNumber}${seatLetter}`,
    cabin,
    price,
    feature,
    isAvailable,
  };

  return seatInfo;
};

export const generateSeatMap = (configuration: SeatConfiguration): Array<SeatMapRow> => {
  type OrderedRowEntry = {
    row: SeatMapRow;
    sortKey: number;
    rank: number;
    overrideOrder: number;
    sequence: number;
  };

  const orderedRows: Array<OrderedRowEntry> = [];
  let sequence = 0;

  const pushRow = (row: SeatMapRow, sortKey: number, rank: number, overrideOrder: number) => {
    orderedRows.push({ row, sortKey, rank, overrideOrder, sequence });
    sequence += 1;
  };

  configuration.cabins.forEach((cabinConfig) => {
    const cabinOverrides: Array<CabinOverridesPayload['overrides'][number]> = cabinConfig.overrides ?? [];
    const cabinRowNumbers = expandRowSelection(cabinConfig.rowSelection);
    const floatingFacilities = collectFloatingFacilityRows(cabinOverrides, cabinConfig.rowSelection);

    for (const rowNumber of cabinRowNumbers) {
      const { primaryLayout, facilityRowsBefore, facilityRowsAfter } = resolveRowLayouts(
        cabinConfig.seatLayout,
        cabinOverrides,
        rowNumber,
      );

      const seatLetters = extractSeatLetters(primaryLayout);
      const seats = seatLetters.map((seatLetter) =>
        buildSeatInfo(
          cabinConfig.cabin,
          rowNumber,
          seatLetter,
          resolveSeatOverrides(cabinOverrides, rowNumber, seatLetter),
        ),
      );

      facilityRowsBefore.forEach((facility, index) => {
        pushRow(
          {
            rowId: `${cabinConfig.cabin}-${rowNumber}-facility-before-${facility.overrideOrder}-${index}`,
            rowNumber,
            cabin: cabinConfig.cabin,
            seats: [],
            layout: facility.layout,
            isFacilityRow: true,
          },
          rowNumber,
          0,
          facility.overrideOrder,
        );
      });

      pushRow(
        {
          rowId: `${cabinConfig.cabin}-${rowNumber}-seats`,
          rowNumber,
          cabin: cabinConfig.cabin,
          seats,
          layout: primaryLayout,
        },
        rowNumber,
        1,
        0,
      );

      facilityRowsAfter.forEach((facility, index) => {
        pushRow(
          {
            rowId: `${cabinConfig.cabin}-${rowNumber}-facility-after-${facility.overrideOrder}-${index}`,
            rowNumber,
            cabin: cabinConfig.cabin,
            seats: [],
            layout: facility.layout,
            isFacilityRow: true,
          },
          rowNumber,
          2,
          facility.overrideOrder,
        );
      });
    }

    floatingFacilities.forEach((facility, index) => {
      pushRow(
        {
          rowId: `${cabinConfig.cabin}-facility-${facility.rowNumber}-${facility.overrideOrder}-${index}`,
          rowNumber: facility.rowNumber,
          cabin: cabinConfig.cabin,
          seats: [],
          layout: facility.layout,
          isFacilityRow: true,
        },
        facility.rowNumber,
        1,
        facility.overrideOrder,
      );
    });
  });

  orderedRows.sort((a, b) => {
    if (a.sortKey !== b.sortKey) {
      return a.sortKey - b.sortKey;
    }

    if (a.rank !== b.rank) {
      return a.rank - b.rank;
    }

    if (a.overrideOrder !== b.overrideOrder) {
      return a.overrideOrder - b.overrideOrder;
    }

    return a.sequence - b.sequence;
  });

  return orderedRows.map((entry) => entry.row);
};
